/// <reference types="vite/client" />

/**
 * Axios API client with JWT Bearer token injection.
 * Mirrors the pattern used in frontend-sales.
 */

import axios, { type AxiosRequestConfig, type AxiosResponse, AxiosError } from 'axios';

function getApiBaseUrl(): string {
  // Always prefer the explicit env var — covers both local dev and remote deployments
  // where the frontend port differs from the backend port.
  const envUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ?? '';
  if (envUrl) return envUrl.replace(/\/$/, '');
  // Fallback: same origin (works when the backend serves the frontend from the same port).
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'http://localhost:8000';
}

export const API_BASE_URL = getApiBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  // Required so the V2 backend's httpOnly refresh cookie (tracit_refresh)
  // is stored on login and sent back on POST /api/v1/auth/refresh.
  withCredentials: true,
});

apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  error => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
    }
    return Promise.reject(error);
  }
);

export async function apiCall<T>(
  config: AxiosRequestConfig
): Promise<{ data: T; error: null } | { data: null; error: string }> {
  try {
    const response = await apiClient(config);
    return { data: response.data as T, error: null };
  } catch (error) {
    const message =
      error instanceof AxiosError
        ? (error.response?.data as { detail?: string })?.detail ?? error.message
        : 'Unknown error occurred';
    return { data: null, error: message };
  }
}

export default apiClient;
